/**
 * Compliance Engine — Pure Functions
 *
 * Extracts duplicated compliance-checking logic from UI components into
 * a pure function module (no DB calls, no side effects).
 *
 * Source of truth: ComplianceSummaryTab.tsx (lines 63-128) expanded to
 * compare against both price book and ticket where applicable.
 */
/** Map a persisted DB row to the engine's result type. */
export function fromDbRow(c) {
    return {
        invoiceItemId: c.invoice_item_id,
        ticketItemId: c.ticket_item_id,
        priceBookItemId: c.price_book_item_id,
        hasPriceBook: c.has_price_book,
        hasTicket: c.has_ticket,
        qtyMismatchOnTicketItem: c.qty_mismatch_on_ticket_item,
        priceMismatchOnPriceBookItem: c.price_mismatch_on_price_book_item,
        priceMismatchOnTicketItem: c.price_mismatch_on_ticket_item,
        totalMismatchOnTicketItem: c.total_mismatch_on_ticket_item,
        missingPriceBookItem: c.missing_price_book_item,
        missingTicketItem: c.missing_ticket_item,
        hasMismatch: c.has_mismatch,
        qtyOverchargeFromTicketItem: Number(c.qty_overcharge_from_ticket_item) || 0,
        priceOverchargeFromPriceBookItem: Number(c.price_overcharge_from_price_book_item) || 0,
        priceOverchargeFromTicketItem: Number(c.price_overcharge_from_ticket_item) || 0,
        totalOverchargeFromTicketItem: Number(c.total_overcharge_from_ticket_item) || 0,
        noPriceBookItemOvercharge: Number(c.no_price_book_item_overcharge) || 0,
        noTicketItemOvercharge: Number(c.no_ticket_item_overcharge) || 0,
        invoiceDiscountedPrice: c.invoice_discounted_price != null ? Number(c.invoice_discounted_price) : null,
        priceBookNetPrice: c.price_book_net_price != null ? Number(c.price_book_net_price) : null,
        ticketDiscountedPrice: c.ticket_discounted_price != null ? Number(c.ticket_discounted_price) : null,
        invoiceQty: c.invoice_qty != null ? Number(c.invoice_qty) : null,
        ticketQty: c.ticket_qty != null ? Number(c.ticket_qty) : null,
        invoiceLineTotal: c.invoice_line_total != null ? Number(c.invoice_line_total) : null,
        ticketLineTotal: c.ticket_line_total != null ? Number(c.ticket_line_total) : null,
    };
}
// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
/** Discounted (net) price: price * (1 - discount / 100) */
function discountedPrice(price, discount) {
    return price * (1 - discount / 100);
}
/** Canonical per-item overcharge: clamped sum of all 6 overcharge fields. */
export function computeItemOvercharge(r) {
    return (Math.max(0, r.qtyOverchargeFromTicketItem) +
        Math.max(0, r.priceOverchargeFromPriceBookItem) +
        Math.max(0, r.priceOverchargeFromTicketItem) +
        Math.max(0, r.totalOverchargeFromTicketItem) +
        Math.max(0, r.noPriceBookItemOvercharge) +
        Math.max(0, r.noTicketItemOvercharge));
}
const PRICE_TOLERANCE = 0.01;
// ---------------------------------------------------------------------------
// Core engine
// ---------------------------------------------------------------------------
/**
 * Run compliance checks for every invoice item.
 *
 * Matching logic (identical to existing UI):
 * - Match by `price_book_item_id` first, then fall back to `item_code` / `itemcode`
 * - Price tolerance: > $0.01 absolute difference
 */
export function checkAllItems(invoiceItems, ticketItems, priceBookItems, context) {
    return invoiceItems.map((invItem) => {
        // 1. Match ticket item
        const ticketItem = ticketItems.find((ti) => (invItem.price_book_item_id &&
            ti.price_book_item_id === invItem.price_book_item_id) ||
            (invItem.item_code && ti.item_code === invItem.item_code)) ?? null;
        // 2. Match price book item
        const pbItem = invItem.price_book_item_id
            ? priceBookItems.find(pbi => pbi.id === invItem.price_book_item_id) ?? null
            : null;
        // Derived values
        const invoiceQty = invItem.quantity ?? 0;
        const ticketQty = ticketItem?.quantity ?? 0;
        const invoicePrice = invItem.unit_price ?? 0;
        const invoiceDiscount = invItem.discount ?? 0;
        const invDiscPrice = discountedPrice(invoicePrice, invoiceDiscount);
        const pbNetPrice = pbItem !== null
            ? discountedPrice(pbItem.price, pbItem.discount)
            : null;
        const ticketPrice = ticketItem?.unit_price ?? 0;
        const ticketDiscount = ticketItem?.discount ?? 0;
        const ticketDiscPrice = ticketItem !== null
            ? discountedPrice(ticketPrice, ticketDiscount)
            : null;
        const invoiceLineTotal = invItem.line_total ?? 0;
        const ticketLineTotal = ticketItem?.line_total ?? 0;
        const ticketHasPricing = ticketItem !== null &&
            (ticketItem.unit_price !== null || ticketItem.line_total !== null);
        // 3. Mismatch flags
        const qtyMismatchOnTicketItem = ticketItem !== null && invoiceQty !== ticketQty;
        const priceMismatchOnPriceBookItem = pbItem !== null &&
            pbNetPrice !== null &&
            Math.abs(invDiscPrice - pbNetPrice) > PRICE_TOLERANCE;
        const priceMismatchOnTicketItem = ticketHasPricing &&
            ticketDiscPrice !== null &&
            Math.abs(invDiscPrice - ticketDiscPrice) > PRICE_TOLERANCE;
        const totalMismatchOnTicketItem = ticketHasPricing &&
            Math.abs(invoiceLineTotal - ticketLineTotal) > PRICE_TOLERANCE;
        // 4. Missing item flags
        const missingPriceBookItem = context.hasPriceBook && pbItem === null;
        const missingTicketItem = context.hasTicket && ticketItem === null;
        // 5. Rolled-up flag
        const hasMismatch = qtyMismatchOnTicketItem ||
            priceMismatchOnPriceBookItem ||
            priceMismatchOnTicketItem ||
            totalMismatchOnTicketItem ||
            missingPriceBookItem ||
            missingTicketItem;
        // 6. Overcharge amounts
        const qtyOverchargeFromTicketItem = qtyMismatchOnTicketItem
            ? (invoiceQty - ticketQty) * invDiscPrice
            : 0;
        const priceOverchargeFromPriceBookItem = priceMismatchOnPriceBookItem && pbNetPrice !== null
            ? (invDiscPrice - pbNetPrice) * invoiceQty
            : 0;
        const priceOverchargeFromTicketItem = priceMismatchOnTicketItem && ticketDiscPrice !== null
            ? (invDiscPrice - ticketDiscPrice) * invoiceQty
            : 0;
        const totalOverchargeFromTicketItem = totalMismatchOnTicketItem
            ? invoiceLineTotal - ticketLineTotal
            : 0;
        const noPriceBookItemOvercharge = missingPriceBookItem
            ? invoiceLineTotal
            : 0;
        const noTicketItemOvercharge = missingTicketItem ? invoiceLineTotal : 0;
        return {
            invoiceItemId: invItem.id,
            ticketItemId: ticketItem?.id ?? null,
            priceBookItemId: pbItem?.id ?? null,
            hasPriceBook: context.hasPriceBook,
            hasTicket: context.hasTicket,
            qtyMismatchOnTicketItem,
            priceMismatchOnPriceBookItem,
            priceMismatchOnTicketItem,
            totalMismatchOnTicketItem,
            missingPriceBookItem,
            missingTicketItem,
            hasMismatch,
            qtyOverchargeFromTicketItem,
            priceOverchargeFromPriceBookItem,
            priceOverchargeFromTicketItem,
            totalOverchargeFromTicketItem,
            noPriceBookItemOvercharge,
            noTicketItemOvercharge,
            invoiceDiscountedPrice: invItem.unit_price !== null ? invDiscPrice : null,
            priceBookNetPrice: pbNetPrice,
            ticketDiscountedPrice: ticketDiscPrice,
            invoiceQty: invItem.quantity,
            ticketQty: ticketItem?.quantity ?? null,
            invoiceLineTotal: invItem.line_total,
            ticketLineTotal: ticketItem?.line_total ?? null,
        };
    });
}
/**
 * Aggregate a list of per-item check results into a summary.
 * Overcharges are clamped to >= 0 per item before summing (negative = undercharge, excluded).
 */
export function summarize(results) {
    let matchedCount = 0;
    let mismatchCount = 0;
    let unmatchedCount = 0;
    let qtyMismatchOnTicketCount = 0;
    let priceMismatchOnPbCount = 0;
    let priceMismatchOnTicketCount = 0;
    let totalMismatchOnTicketCount = 0;
    let totalQtyOverchargeFromTicket = 0;
    let totalPriceOverchargeFromPb = 0;
    let totalPriceOverchargeFromTicket = 0;
    let totalOverchargeFromTicket = 0;
    let totalNoPriceBookItemOvercharge = 0;
    let totalNoTicketItemOvercharge = 0;
    let totalOvercharge = 0;
    for (const r of results) {
        if (r.hasMismatch) {
            mismatchCount++;
        }
        else if (r.ticketItemId || r.priceBookItemId) {
            matchedCount++;
        }
        if (r.missingPriceBookItem || r.missingTicketItem) {
            unmatchedCount++;
        }
        if (r.qtyMismatchOnTicketItem)
            qtyMismatchOnTicketCount++;
        if (r.priceMismatchOnPriceBookItem)
            priceMismatchOnPbCount++;
        if (r.priceMismatchOnTicketItem)
            priceMismatchOnTicketCount++;
        if (r.totalMismatchOnTicketItem)
            totalMismatchOnTicketCount++;
        totalQtyOverchargeFromTicket += Math.max(0, r.qtyOverchargeFromTicketItem);
        totalPriceOverchargeFromPb += Math.max(0, r.priceOverchargeFromPriceBookItem);
        totalPriceOverchargeFromTicket += Math.max(0, r.priceOverchargeFromTicketItem);
        totalOverchargeFromTicket += Math.max(0, r.totalOverchargeFromTicketItem);
        totalNoPriceBookItemOvercharge += Math.max(0, r.noPriceBookItemOvercharge);
        totalNoTicketItemOvercharge += Math.max(0, r.noTicketItemOvercharge);
        totalOvercharge += computeItemOvercharge(r);
    }
    return {
        totalItems: results.length,
        matchedCount,
        mismatchCount,
        unmatchedCount,
        qtyMismatchOnTicketCount,
        priceMismatchOnPbCount,
        priceMismatchOnTicketCount,
        totalMismatchOnTicketCount,
        totalQtyOverchargeFromTicket,
        totalPriceOverchargeFromPb,
        totalPriceOverchargeFromTicket,
        totalOverchargeFromTicket,
        totalNoPriceBookItemOvercharge,
        totalNoTicketItemOvercharge,
        totalOvercharge,
    };
}
//# sourceMappingURL=complianceEngine.js.map